import React, { useEffect } from 'react';
import { Divider } from '@mui/material';
import {
  useUpdateContentLibrary,
  ContentLibraryAction,
  useExportContent,
  useCompareProductKeywords
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useContentGenerate';
import { getQueryParamValue } from 'src/utils/app';
import { useAppSelector, useMainTimePeriod, useSnackbar, useLocation } from 'src/utils/Hooks/typedHooks';
import {
  KeywordContent,
  KeywordWeight,
  ContentLibraryManagementRequest
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import { extractSectionData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/utils';
import {
  ContentCard,
  ContentComparisonHeader,
  NoRoundedBottomContentCard
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/ContentComparisonCard';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import colors from 'src/utils/colors';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import {
  useContentGenReviewStore,
  useKeywordsDisplayStore,
  useContentViewStore
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { ContentGenSingleReviewSkeleton } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/ContentSkeletons';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { buildTabAndSubtabLink } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { BEACON_TABS, BEACON_SUBTABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import ButtonDropdown from './ButtonDropdown';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import ContentGenSingleReviewHeader from './components/ContentGenSingleReviewHeader';
import ProfileForm from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Profile/ProfileForm';
import { FormProvider, useForm } from 'react-hook-form';
import {
  FinalFormProfile,
  FinalFormProfileSchema
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/types/profileTypes';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetSingleProfile } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/useProfiles';

type ActionBarProps = {
  onBack: () => void;
  onExport: () => void;
  onNext?: () => void;
  onArchive?: () => void;
  onSave?: (option: { id: string; label: string }) => void;
  hasGeneratedContent: boolean;
  canGoNext: boolean;
  isSaving: boolean;
  isExporting: boolean;
  getSaveMenuOptions: () => Array<{ id: string; label: string }>;
};

const ActionBar = ({
  onBack,
  onExport,
  onNext,
  onArchive,
  onSave,
  hasGeneratedContent,
  canGoNext,
  isSaving,
  isExporting,
  getSaveMenuOptions
}: ActionBarProps) => (
  <Flex
    justifyContent="space-between"
    sx={{
      marginTop: '64px'
    }}
  >
    <Flex gap={16}>
      <SlButton
        buttonSx={{
          border: `1px solid ${colors.graysSecondary}`
        }}
        onClick={(e) => {
          e.preventDefault();
          onBack();
        }}
      >
        Back
      </SlButton>
      {hasGeneratedContent && onArchive && (
        <SlButton
          buttonSx={{
            backgroundColor: colors.graysPrimary
          }}
          onClick={onArchive}
          disabled={isSaving}
        >
          Archive content
        </SlButton>
      )}
    </Flex>
    <Flex gap={16}>
      <SlButton
        buttonSx={{
          border: `1px solid ${colors.graysSecondary}`
        }}
        onClick={onExport}
        disabled={isExporting}
      >
        {isExporting ? 'Exporting...' : 'Export'}
      </SlButton>
      {hasGeneratedContent && onSave && (
        <ButtonDropdown
          label="Save content"
          options={getSaveMenuOptions()}
          onChange={onSave}
          variant="contained"
          disabled={isSaving}
        />
      )}
      {canGoNext && onNext && (
        <SlButton variant="contained" onClick={onNext} disabled={isSaving}>
          Next
        </SlButton>
      )}
    </Flex>
  </Flex>
);

type ContentGenProfileViewProps = {
  actionBarProps: ActionBarProps;
  profile?: any;
  isLoadingProfile?: boolean;
};

const ContentGenProfileView = ({ actionBarProps, profile, isLoadingProfile }: ContentGenProfileViewProps) => {
  // Set up form for ProfileForm with default values for read-only mode
  const methods = useForm<FinalFormProfile>({
    mode: 'all',
    resolver: zodResolver(FinalFormProfileSchema),
    defaultValues: profile
      ? {
          variant: 'edit' as const,
          ...profile
        }
      : {
          variant: 'edit' as const,
          profileName: 'Loading profile...',
          profileDescription: 'Loading profile description...',
          settings: {
            bulletPointsCount: 5,
            characterLimits: {
              titles: 150,
              bullets: 200,
              descriptions: 1800
            },
            aiInstructions: 'Loading AI instructions...',
            voice: {
              type: 'Informative'
            },
            tone: {
              type: 'Friendly'
            },
            keywords: {
              inclusion: [],
              exclusion: []
            }
          }
        }
  });

  // Update form when profile data is loaded
  React.useEffect(() => {
    if (profile && !isLoadingProfile) {
      methods.reset({
        variant: 'edit' as const,
        ...profile
      });
    }
  }, [profile, isLoadingProfile, methods]);

  return (
    <>
      <FormProvider {...methods}>
        <ProfileForm readOnly={true} onBack={actionBarProps.onBack} />
      </FormProvider>
      <ActionBar {...actionBarProps} />
    </>
  );
};

type ContentGenComparisonViewProps = {
  currentContentPhrasesKeywords: Record<string, { phrases: KeywordContent[]; keywords: KeywordWeight[] }>;
  generatedContentPhrasesKeywords: Record<string, { phrases: KeywordContent[]; keywords: KeywordWeight[] }>;
  actionBarProps: ActionBarProps;
};

const ContentGenComparisonView = ({
  currentContentPhrasesKeywords,
  generatedContentPhrasesKeywords,
  actionBarProps
}: ContentGenComparisonViewProps) => (
  <>
    <Flex gap={16}>
      <ContentCard title="Title" content={currentContentPhrasesKeywords?.Title?.phrases} />
      <ContentCard title="Title" content={generatedContentPhrasesKeywords?.Title?.phrases} />
    </Flex>
    <Divider sx={{ margin: '32px 0px' }} />
    <AllBulletsComparison
      currentContentPhrasesKeywords={currentContentPhrasesKeywords}
      generatedContentPhrasesKeywords={generatedContentPhrasesKeywords}
    />
    <Flex gap={16}>
      <ContentCard
        title="Description"
        content={currentContentPhrasesKeywords?.Description?.phrases}
        flexHeight="828px"
        boxHeight="752px"
      />
      <Divider sx={{ margin: '32px 0px' }} />
      <NoRoundedBottomContentCard
        title="Description"
        content={generatedContentPhrasesKeywords?.Description?.phrases}
        flexHeight="828px"
        boxHeight="752px"
      />
    </Flex>
    <ActionBar {...actionBarProps} />
  </>
);

type ContentGenCurrentOnlyViewProps = {
  currentContentPhrasesKeywords: Record<string, { phrases: KeywordContent[]; keywords: KeywordWeight[] }>;
  actionBarProps: ActionBarProps;
};

const ContentGenCurrentOnlyView = ({
  currentContentPhrasesKeywords,
  actionBarProps
}: ContentGenCurrentOnlyViewProps) => {
  const getAllBulletIndices = () => {
    const indices: number[] = [];
    const allKeys = Object.keys(currentContentPhrasesKeywords || {});

    allKeys.forEach((key) => {
      if (key.toLowerCase()?.startsWith('bullet ')) {
        const indexStr = key.split(' ')[1];
        const index = parseInt(indexStr, 10);
        if (!isNaN(index) && !indices.includes(index - 1)) {
          indices.push(index - 1);
        }
      }
    });

    return indices.sort((a, b) => a - b);
  };

  const bulletIndices = getAllBulletIndices();

  return (
    <>
      {/* Show only current content when no generated content is available */}
      <ContentCard title="Title" content={currentContentPhrasesKeywords?.Title?.phrases} />
      <Divider sx={{ margin: '32px 0px' }} />

      {/* Show current bullets only */}
      {bulletIndices.map((index) => (
        <React.Fragment key={`current-bullet-${index}`}>
          <ContentCard
            title={`Bullet ${index + 1}`}
            content={currentContentPhrasesKeywords?.[`Bullet ${index + 1}`]?.phrases}
            placeholderText={`This product does not have a ${index + 1} bullet.`}
          />
          <Divider sx={{ margin: '32px 0px' }} />
        </React.Fragment>
      ))}

      <ContentCard
        title="Description"
        content={currentContentPhrasesKeywords?.Description?.phrases}
        flexHeight="828px"
        boxHeight="752px"
      />
      <ActionBar {...actionBarProps} />
    </>
  );
};

type AllBulletsComparisonProps = {
  currentContentPhrasesKeywords: Record<string, { phrases: KeywordContent[]; keywords: KeywordWeight[] }>;
  generatedContentPhrasesKeywords: Record<string, { phrases: KeywordContent[]; keywords: KeywordWeight[] }>;
};

const AllBulletsComparison = ({
  currentContentPhrasesKeywords,
  generatedContentPhrasesKeywords
}: AllBulletsComparisonProps) => {
  const getAllBulletIndices = () => {
    const indices = new Set<number>();

    const allKeys = [
      ...Object.keys(currentContentPhrasesKeywords || {}),
      ...Object.keys(generatedContentPhrasesKeywords || {})
    ];

    allKeys.forEach((key) => {
      if (key.toLowerCase()?.startsWith('bullet ')) {
        const indexStr = key.split(' ')[1];
        const index = parseInt(indexStr, 10);
        if (!isNaN(index)) {
          indices.add(index - 1);
        }
      }
    });

    return Array.from(indices).sort((a, b) => a - b);
  };

  const bulletIndices = getAllBulletIndices();

  return (
    <>
      {bulletIndices.map((index) => (
        <>
          <Flex key={`bullet-${index}`} gap={16}>
            <ContentCard
              title={`Bullet ${index + 1}`}
              content={currentContentPhrasesKeywords?.[`Bullet ${index + 1}`]?.phrases}
              placeholderText={`This product does not have a ${index + 1} bullet.`}
            />
            <ContentCard
              title={`Bullet ${index + 1}`}
              content={generatedContentPhrasesKeywords?.[`Bullet ${index + 1}`]?.phrases}
              placeholderText={`No content was generated for bullet ${index + 1}.`}
            />
          </Flex>
          <Divider sx={{ margin: '32px 0px' }} />
        </>
      ))}
    </>
  );
};

const ContentGenMultiReview = () => {
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const app = useAppSelector((state) => state.app);

  const retailerId = getQueryParamValue('rid', '');
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);
  const { endWeek } = useMainTimePeriod();

  const {
    getCurrentContentGenId,
    getCurrentContentGenItem,
    getCurrentContentGenItemIndex,
    goToNextContentGenItem,
    goToPreviousContentGenItem,
    hasContentGenItems,
    contentGenItemsToReview,
    clearContentGenItems
  } = useContentGenReviewStore();

  const currentContentGenId = getCurrentContentGenId();
  const currentIndex = getCurrentContentGenItemIndex();
  const totalReviews = contentGenItemsToReview.length;
  const currentContentGenItem = getCurrentContentGenItem();

  const {
    currentContent,
    generatedContent,
    formattedCurrentSections,
    formattedNewSections,
    comparisonData,
    isLoading: isLoadingComparison,
    error
  } = useCompareProductKeywords({
    retailerSku: currentContentGenItem?.retailerSku || '',
    retailerId: Number(retailerId),
    weekId: endWeek,
    skuContentGenerationId: currentContentGenItem?.skuContentGenerationId || '',
    beaconClientId: beaconClientId || 0
  });

  const { setShowKeywords } = useKeywordsDisplayStore();
  const { currentView } = useContentViewStore();
  const { updateContentLibrary: saveAsDraft, isPending: isSavingDraft } = useUpdateContentLibrary('draft');
  const { updateContentLibrary: saveAsMaster, isPending: isSavingMaster } = useUpdateContentLibrary('master');

  // Fetch profile data for the current content generation item
  const { profile, isLoadingSingleProfile } = useGetSingleProfile(currentContentGenItem?.profileId);
  const { updateContentLibrary: archiveContent, isPending: isArchiving } = useUpdateContentLibrary('archive');
  const { exportContent, isPending: isExporting, isSuccess: isExportSuccess } = useExportContent();
  const { beaconNavigator } = useBeaconNavigator();

  // Track any action in progress
  const isSaving = isSavingDraft || isSavingMaster || isArchiving;

  useEffect(() => {
    setShowKeywords(true);
  }, [setShowKeywords]);

  if (!hasContentGenItems()) {
    const generationsRoute = buildTabAndSubtabLink({
      app,
      location,
      tab: BEACON_TABS.GENERATION,
      subtab: BEACON_SUBTABS.GENERATE
    });
    beaconNavigator.go(generationsRoute);
    return null;
  }

  // Handle navigation between reviews
  const handlePreviousReview = () => {
    goToPreviousContentGenItem();
  };

  const handleNextReview = () => {
    goToNextContentGenItem();
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      const previousIndex = currentIndex - 1;
      const previousItem = contentGenItemsToReview[previousIndex];

      handlePreviousReview();

      if (previousItem?.stacklineSku) {
        beaconNavigator.updateSearchParams({
          tab: BEACON_TABS.GENERATION,
          subtab: BEACON_SUBTABS.GENERATION_REVIEW,
          sku: previousItem.stacklineSku,
          contentGenId: previousItem.skuContentGenerationId
        });
        beaconNavigator.go();
      }
    } else {
      // Navigate back to generate page
      beaconNavigator.updateSearchParams({
        tab: BEACON_TABS.GENERATION,
        subtab: BEACON_SUBTABS.GENERATE
      });
      beaconNavigator.go();
    }
  };

  const canGoNext = currentIndex < totalReviews - 1;

  // Simple loading check - wait for current content to be available
  // Don't block on generated content since it might not exist
  if (isLoadingComparison || !currentContent) {
    return (
      <BasePageContainer>
        <ContentGenSingleReviewSkeleton />
      </BasePageContainer>
    );
  }

  const newContent = generatedContent?.[currentContentGenId];
  const hasGeneratedContent = !error && !!newContent;

  if (error) {
    const errorMessage = error?.message || 'Error loading content.';
    return (
      <BasePageContainer>
        <div>Error: {errorMessage}</div>
      </BasePageContainer>
    );
  }

  const currentContentPhrasesKeywords = formattedCurrentSections
    ? extractSectionData({ weightedKeywordPhrases: { sections: formattedCurrentSections } })
    : {};

  const generatedContentPhrasesKeywords =
    hasGeneratedContent && formattedNewSections
      ? extractSectionData({ weightedKeywordPhrases: { sections: formattedNewSections } })
      : {};

  const handleUpdateContentLibrary = (type: ContentLibraryAction) => {
    const generatedContentItem = generatedContent?.[currentContentGenId];
    const contentLibraryManagementRequest: ContentLibraryManagementRequest = {
      skuContentGenerationId: currentContentGenId,
      retailerId: Number(retailerId),
      retailerSku: currentContentGenItem?.retailerSku || '',
      title: generatedContentItem?.content?.title,
      description: generatedContentItem?.content?.description,
      bullets: generatedContentItem?.content?.featureBullets
    };

    switch (type) {
      case 'draft':
        saveAsDraft(contentLibraryManagementRequest);
        break;
      case 'master':
        saveAsMaster(contentLibraryManagementRequest);
        break;
      case 'archive':
        archiveContent(contentLibraryManagementRequest);
        break;
    }
  };

  const getSaveMenuOptions = () => {
    return [
      {
        id: 'saveToLibrary',
        label: 'Save to library'
      },
      {
        id: 'saveToMaster',
        label: 'Save as master'
      }
    ];
  };

  const handleSaveOption = (option: { id: string; label: string }) => {
    if (option.id === 'saveToLibrary') {
      handleUpdateContentLibrary('draft');
    } else if (option.id === 'saveToMaster') {
      handleUpdateContentLibrary('master');
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      // Get the next item's stacklineSku before navigating
      const nextIndex = currentIndex + 1;
      const nextItem = contentGenItemsToReview[nextIndex];

      // Navigate to next review
      handleNextReview();

      // Update the sku query parameter
      if (nextItem?.stacklineSku) {
        beaconNavigator.updateSearchParams({
          tab: BEACON_TABS.GENERATION,
          subtab: BEACON_SUBTABS.GENERATION_REVIEW,
          sku: nextItem.stacklineSku,
          contentGenId: nextItem.skuContentGenerationId
        });
        beaconNavigator.go();
      }
    } else {
      // This is the last review, handle completion
      showSnackbar({
        message: 'All reviews completed!',
        type: 'success'
      });
      // Clear the content generation IDs and navigate back to generate page
      clearContentGenItems();
      beaconNavigator.updateSearchParams({
        tab: BEACON_TABS.GENERATION,
        subtab: BEACON_SUBTABS.GENERATE
      });
      beaconNavigator.go();
    }
  };

  const handleExport = () => {
    // Get all content generation IDs from the review store
    const allContentGenIds = contentGenItemsToReview.map((item) => item.skuContentGenerationId);

    if (allContentGenIds.length === 0) {
      showSnackbar({ message: 'No content to export.', type: 'error' });
      return;
    }

    exportContent({
      retailerId: Number(retailerId),
      skuContentGenerationIds: allContentGenIds
    });
  };

  const getActionBarProps = (): ActionBarProps => ({
    onBack: handleBack,
    onExport: handleExport,
    onNext: canGoNext ? handleNext : undefined,
    onArchive: hasGeneratedContent ? () => handleUpdateContentLibrary('archive') : undefined,
    onSave: hasGeneratedContent ? handleSaveOption : undefined,
    hasGeneratedContent,
    canGoNext,
    isSaving,
    isExporting,
    getSaveMenuOptions
  });

  return (
    <BasePageContainer>
      <ContentGenSingleReviewHeader
        comparisonData={comparisonData?.type === 'comparison' ? comparisonData.data : null}
        currentContent={currentContent}
        generatedContent={hasGeneratedContent ? generatedContent : null}
        currentContentGenId={currentContentGenId}
      />
      {hasGeneratedContent && <ContentComparisonHeader />}

      {currentView === 'profile' ? (
        <ContentGenProfileView
          actionBarProps={getActionBarProps()}
          profile={profile}
          isLoadingProfile={isLoadingSingleProfile}
        />
      ) : hasGeneratedContent ? (
        <ContentGenComparisonView
          currentContentPhrasesKeywords={currentContentPhrasesKeywords}
          generatedContentPhrasesKeywords={generatedContentPhrasesKeywords}
          actionBarProps={getActionBarProps()}
        />
      ) : (
        <ContentGenCurrentOnlyView
          currentContentPhrasesKeywords={currentContentPhrasesKeywords}
          actionBarProps={getActionBarProps()}
        />
      )}
    </BasePageContainer>
  );
};

export default ContentGenMultiReview;
